/* eslint-disable import/no-named-as-default */
import { useHasBillingAccess } from 'components/billing/common/hooks/useHasBillingAccess';
import IntercomManager from 'components/chat/IntercomManager';
import { GlobalModalProvider } from 'components/GlobalModal/globalModal';
import PermissionReadiness from 'components/Global/PermissionReadiness';
import useAuth from 'hooks/useAuth';
import { WebSocketProvider } from 'components/responses/hooks/useWebSocket';
import { AgentActionsProvider } from 'controllers/contexts/agentActions';
import { AnnouncementsProvider } from 'controllers/contexts/announcements';
import { FullScreenEditorProvider } from 'controllers/contexts/fullScreenEditor';
import { SystemEventVisibilityProvider } from 'controllers/contexts/systemEventVisibility';
import { AcceptedTOSProvider } from 'controllers/contexts/termsOfService';
import PropTypes from 'prop-types';
import { ShopperStatusProvider } from 'providers';
import { ProgressiveOnboardingProvider } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BillingProvider, UserContext } from '../../controllers/contexts';
import { FeatureFlagsProvider } from '../../controllers/contexts/featureFlags';
import { LabsFeatureProvider as LabsFeatureFlagsProvider } from '../../controllers/contexts/labsFeatures';
import { LegacyGlobalNotificationsProvider } from '../../controllers/contexts/legacyGlobalNotifications';
import { MMSStatusProvider } from '../../controllers/contexts/mmsStatus';
import { NotesProvider } from '../../controllers/contexts/notes';
import Notifications from '../account/notifications';
import { UsageBillingProvider } from '../billing/context/usageBilling';
import { GlobalBannersProvider } from '../GlobalBanners/globalBanners';
import ModalContainer from '../layout/ModalContainer';

const BillingProviderWithPermission = ({ user, children }) => {
  const hasBillingAccess = useHasBillingAccess();
  return (
    <BillingProvider user={user} hasBillingAccess={hasBillingAccess}>
      {children}
    </BillingProvider>
  );
};

BillingProviderWithPermission.propTypes = {
  user: PropTypes.any,
  children: PropTypes.node.isRequired,
};

BillingProviderWithPermission.defaultProps = {
  user: false,
};

/**
 * Wrapper for injecting global resources (via context api) that are
 * used throught the application e.g. users
 *
 * @component
 * @example
 * return (
 *   <GlobalAuthenticatedContexts>
 *     <ExampleAuthenticatedComponents />
 *   </GlobalAuthenticatedContexts>
 * )
 */
const GlobalAuthenticatedContexts = ({
  user,
  shops,
  isFetchingShops,
  getUserInfo,
  updateUserInfo,
  updateShops,
  children,
}) => {
  const { isHistorySession } = useAuth();
  return (
    <DndProvider backend={HTML5Backend}>
      <UserContext.Provider
        value={{
          user,
          shops,
          isFetchingShops,
          getUserInfo,
          updateUserInfo,
          updateShops,
        }}
      >
        <FeatureFlagsProvider>
          <LabsFeatureFlagsProvider>
            <PermissionReadiness>
              <IntercomManager>
                <WebSocketProvider>
                  <AcceptedTOSProvider>
                    <BillingProviderWithPermission user={user}>
                      <NotesProvider>
                        <LegacyGlobalNotificationsProvider>
                          <ShopperStatusProvider>
                            <GlobalModalProvider>
                              <GlobalBannersProvider>
                                <AnnouncementsProvider>
                                  <UsageBillingProvider>
                                    <MMSStatusProvider>
                                      <FullScreenEditorProvider>
                                        <SystemEventVisibilityProvider>
                                          <AgentActionsProvider>
                                            <>
                                              {!isHistorySession && (
                                                <Notifications
                                                  shopId={user.shop_id}
                                                />
                                              )}
                                              <ProgressiveOnboardingProvider>
                                                <ModalContainer>
                                                  {children}
                                                </ModalContainer>
                                              </ProgressiveOnboardingProvider>
                                            </>
                                          </AgentActionsProvider>
                                        </SystemEventVisibilityProvider>
                                      </FullScreenEditorProvider>
                                    </MMSStatusProvider>
                                  </UsageBillingProvider>
                                </AnnouncementsProvider>
                              </GlobalBannersProvider>
                            </GlobalModalProvider>
                          </ShopperStatusProvider>
                        </LegacyGlobalNotificationsProvider>
                      </NotesProvider>
                    </BillingProviderWithPermission>
                  </AcceptedTOSProvider>
                </WebSocketProvider>
              </IntercomManager>
            </PermissionReadiness>
          </LabsFeatureFlagsProvider>
        </FeatureFlagsProvider>
      </UserContext.Provider>
    </DndProvider>
  );
};

GlobalAuthenticatedContexts.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.array])
    .isRequired,
  user: PropTypes.any,
  shops: PropTypes.array,
  isFetchingShops: PropTypes.bool,
  getUserInfo: PropTypes.func,
  updateUserInfo: PropTypes.func,
  updateShops: PropTypes.func,
};

GlobalAuthenticatedContexts.defaultProps = {
  user: false,
  shops: [],
  isFetchingShops: false,
  getUserInfo: () => {},
  updateUserInfo: () => {},
  updateShops: () => {},
};

export default GlobalAuthenticatedContexts;
